import { supabase } from '@/shared/lib/supabase';
import { logger } from '@/shared/lib/logger';

interface ValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Chama Edge Function para validar unicidade (bypassa RLS)
 */
async function callValidateUniquenessEdgeFunction(
  cpfCnpj?: string,
  phone?: string,
  excludeUserId?: string
): Promise<{ cpf_exists: boolean; phone_exists: boolean } | null> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    const response = await fetch(`${supabaseUrl}/functions/v1/validate-uniqueness`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({
        cpf_cnpj: cpfCnpj,
        phone: phone,
        exclude_user_id: excludeUserId,
      }),
    });

    if (!response.ok) {
      logger.warn('Edge function returned non-ok status', { status: response.status });
      return null;
    }

    const data = await response.json();
    
    if (data.success) {
      return {
        cpf_exists: data.cpf_exists,
        phone_exists: data.phone_exists,
      };
    }
    
    return null;
  } catch (error) {
    logger.warn('Edge function call failed', { error });
    return null;
  }
}

/**
 * Valida se CPF/CNPJ já está cadastrado
 */
export const validateCpfCnpjUnique = async (
  cpfCnpj: string,
  currentUserId?: string
): Promise<ValidationResult> => {
  try {
    // Remove formatação
    const cleanCpfCnpj = cpfCnpj.replace(/\D/g, '');
    
    if (!cleanCpfCnpj) {
      return { isValid: true };
    }

    // MÉTODO 1: Edge Function (mais confiável, bypassa RLS)
    const edgeFunctionResult = await callValidateUniquenessEdgeFunction(cleanCpfCnpj, undefined, currentUserId);
    
    if (edgeFunctionResult !== null) {
      if (edgeFunctionResult.cpf_exists) {
        return {
          isValid: false,
          error: 'CPF/CNPJ já cadastrado em outra conta',
        };
      }
      return { isValid: true };
    }

    // MÉTODO 2: Tentar usar RPC (também bypassa RLS se existir)
    const { data: rpcData, error: rpcError } = await supabase.rpc('check_profile_uniqueness', {
      check_cpf: cleanCpfCnpj
    });

    if (!rpcError && rpcData && rpcData.length > 0) {
      const result = rpcData[0];
      
      if (result.cpf_exists) {
        return {
          isValid: false,
          error: 'CPF/CNPJ já cadastrado em outra conta',
        };
      }
      return { isValid: true };
    }

    // MÉTODO 3: Fallback para query direta (PODE FALHAR devido ao RLS!)
    // IMPORTANTE: Se chegou aqui, a validação pode não funcionar corretamente
    let query = supabase
      .from('user_profiles')
      .select('id, cpf_cnpj')
      .eq('cpf_cnpj', cleanCpfCnpj)
      .limit(1);

    if (currentUserId) {
      query = query.neq('id', currentUserId);
    }

    const { data, error } = await query;

    if (error) {
      logger.error('Error validating CPF/CNPJ', error);
      // Se houve erro na query, assumir que pode existir duplicata por segurança
      return { isValid: false, error: 'Erro ao validar CPF/CNPJ. Tente novamente.' };
    }

    if (data && data.length > 0) {
      return {
        isValid: false,
        error: 'CPF/CNPJ já cadastrado em outra conta',
      };
    }

    return { isValid: true };
  } catch (error) {
    logger.error('Error validating CPF/CNPJ', error);
    return { isValid: false, error: 'Erro ao validar CPF/CNPJ' };
  }
};

/**
 * Valida se telefone já está cadastrado
 */
export const validatePhoneUnique = async (
  phone: string,
  currentUserId?: string
): Promise<ValidationResult> => {
  try {
    // Remove formatação
    const cleanPhone = phone.replace(/\D/g, '');
    
    if (!cleanPhone) {
      return { isValid: true };
    }

    // MÉTODO 1: Edge Function (mais confiável, bypassa RLS)
    const edgeFunctionResult = await callValidateUniquenessEdgeFunction(undefined, cleanPhone, currentUserId);
    
    if (edgeFunctionResult !== null) {
      if (edgeFunctionResult.phone_exists) {
        return {
          isValid: false,
          error: 'Telefone já cadastrado em outra conta',
        };
      }
      return { isValid: true };
    }

    // MÉTODO 2: Tentar usar RPC (também bypassa RLS se existir)
    const { data: rpcData, error: rpcError } = await supabase.rpc('check_profile_uniqueness', {
      check_phone: cleanPhone
    });

    if (!rpcError && rpcData && rpcData.length > 0) {
      const result = rpcData[0];
      
      if (result.phone_exists) {
        return {
          isValid: false,
          error: 'Telefone já cadastrado em outra conta',
        };
      }
      return { isValid: true };
    }

    // MÉTODO 3: Fallback para query direta (PODE FALHAR devido ao RLS!)
    let query = supabase
      .from('user_profiles')
      .select('id, phone')
      .eq('phone', cleanPhone)
      .limit(1);

    if (currentUserId) {
      query = query.neq('id', currentUserId);
    }

    const { data, error } = await query;

    if (error) {
      logger.error('Error validating phone', error);
      return { isValid: false, error: 'Erro ao validar telefone. Tente novamente.' };
    }

    if (data && data.length > 0) {
      return {
        isValid: false,
        error: 'Telefone já cadastrado em outra conta',
      };
    }

    return { isValid: true };
  } catch (error) {
    logger.error('Error validating phone', error);
    return { isValid: false, error: 'Erro ao validar telefone' };
  }
};

/**
 * Valida se email já está cadastrado (verifica auth.users)
 */
export const validateEmailUnique = async (
  email: string
): Promise<ValidationResult> => {
  try {
    if (!email) {
      return { isValid: true };
    }

    return { isValid: true };
  } catch (error) {
    logger.error('Error validating email', error);
    return { isValid: false, error: 'Erro ao validar email' };
  }
};
